/**
 * Form Validation Utilities for UISP Event Forms
 * 
 * This file provides reusable validation functions for event forms
 */

/**
 * Validates basic event form data
 * @param {Object} formData - The event form data to validate
 * @returns {Object} - { isValid: boolean, errors: Array }
 */
export const validateEventForm = (formData) => {
  const errors = [];

  // Required fields validation
  const requiredFields = [
    { field: 'title', message: 'Il titolo dell\'evento è obbligatorio' },
    { field: 'location', message: 'La location è obbligatoria' },
    { field: 'eventDate', message: 'La data dell\'evento è obbligatoria' },
    { field: 'startTime', message: 'L\'orario di inizio è obbligatorio' },
    { field: 'endTime', message: 'L\'orario di fine è obbligatorio' },
    { field: 'category', message: 'La categoria è obbligatoria' }
  ];

  requiredFields.forEach(({ field, message }) => {
    if (!formData[field] || formData[field].trim() === '') {
      errors.push(message);
    }
  });

  // Date validation
  if (formData.eventDate) {
    const currentDate = new Date();
    const eventDate = new Date(formData.eventDate);

    if (eventDate < currentDate && !formData.id) { // Skip for existing events
      errors.push('La data dell\'evento non può essere nel passato');
    }
  }

  // Numeric fields validation
  if (formData.maxParticipants !== '' && (isNaN(Number(formData.maxParticipants)) || Number(formData.maxParticipants) <= 0)) {
    errors.push('Il numero massimo di partecipanti deve essere un numero positivo');
  }

  if (formData.price !== '' && (isNaN(Number(formData.price)) || Number(formData.price) < 0)) {
    errors.push('Il prezzo non può essere negativo');
  }

  // Time validation
  if (formData.startTime && formData.endTime) {
    if (formData.endTime <= formData.startTime) {
      errors.push('L\'orario di fine deve essere successivo all\'orario di inizio');
    }
  }

  // Scope validation (livello evento)
  if (formData.scope !== undefined && formData.scope !== null && formData.scope !== '') {
    const normalizedScope = String(formData.scope).toLowerCase().trim();
    if (!['territorial', 'regional', 'national'].includes(normalizedScope)) {
      errors.push('Il livello evento deve essere Territoriale, Regionale o Nazionale');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates registration fields
 * @param {Array} registrationFields - The registration fields to validate
 * @returns {Object} - { isValid: boolean, errors: Array }
 */
export const validateRegistrationFields = (registrationFields) => {
  const errors = [];

  // Check for duplicate field names
  const fieldNames = new Set();
  const duplicates = [];

  registrationFields.forEach(field => {
    if (field.fieldName) {
      if (fieldNames.has(field.fieldName)) {
        duplicates.push(field.fieldName);
      } else {
        fieldNames.add(field.fieldName);
      }
    }
  });

  if (duplicates.length > 0) {
    errors.push(`I seguenti nomi di campo sono duplicati: ${duplicates.join(', ')}`);
  }

  // Check for empty required fields
  registrationFields.forEach((field, index) => {
    if (!field.fieldName) {
      errors.push(`Il campo #${index + 1} non ha un nome`);
    }

    if (!field.fieldLabel) {
      errors.push(`Il campo #${index + 1} non ha un'etichetta`);
    }

    // Validate options for select/multiselect
    if ((field.fieldType === 'select' || field.fieldType === 'multiselect') &&
      (!field.options || field.options.length === 0 || field.options.some(opt => !opt))) {
      errors.push(`Il campo ${field.fieldLabel || '#' + (index + 1)} ha opzioni vuote o mancanti`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Prepares form data for API submission
 * @param {Object} formData - The form data to prepare
 * @returns {FormData|Object} - Prepared data for submission
 */
export const prepareFormDataForSubmission = (formData) => {
  // Check if we need FormData (for file uploads)
  const hasFiles =
    formData.image ||
    formData.regulation ||
    (formData.documents && formData.documents.length > 0);

  if (hasFiles) {
    // Create FormData for multipart/form-data
    const apiFormData = new FormData();

    // Add basic text fields
    apiFormData.append('title', formData.title);
    apiFormData.append('description', formData.description || '');
    apiFormData.append('location', formData.location);
    apiFormData.append('eventDate', formData.eventDate);
    apiFormData.append('startTime', formData.startTime);
    apiFormData.append('endTime', formData.endTime);
    apiFormData.append('maxParticipants', formData.maxParticipants ? Number(formData.maxParticipants) : 0);
    apiFormData.append('price', formData.price ? Number(formData.price) : 0);
    apiFormData.append('category', formData.category);
    apiFormData.append('scope', formData.scope || 'territorial');
    apiFormData.append('isPublic', formData.isPublic !== undefined ? formData.isPublic : true);

    // Add images if exists
    if (formData.image) {
      apiFormData.append('image', formData.image);
    }

    // Add regulation document if exists
    if (formData.regulation) {
      apiFormData.append('documents', formData.regulation, `Regolamento_${formData.regulation.name}`);
    }

    // Add other documents if they exist
    if (formData.documents && formData.documents.length > 0) {
      Array.from(formData.documents).forEach(doc => {
        apiFormData.append('documents', doc);
      });
    }

    // Add registration fields
    if (formData.registrationFields && formData.registrationFields.length > 0) {
      apiFormData.append('registrationFields', JSON.stringify(formData.registrationFields));
    }

    return apiFormData;
  } else {
    // Return regular JSON object
    return {
      title: formData.title,
      description: formData.description || '',
      location: formData.location,
      eventDate: formData.eventDate,
      startTime: formData.startTime,
      endTime: formData.endTime,
      maxParticipants: formData.maxParticipants ? Number(formData.maxParticipants) : 0,
      price: formData.price ? Number(formData.price) : 0,
      category: formData.category,
      scope: formData.scope || 'territorial',
      isPublic: formData.isPublic !== undefined ? formData.isPublic : true,
      registrationFields: formData.registrationFields || []
    };
  }
};

export default {
  validateEventForm,
  validateRegistrationFields,
  prepareFormDataForSubmission
};
